import { CATALOG_PAGE_CATEGORY } from "~/modules/catalog/variables/CatalogFetchKeys";
import type { CategoryFull, CategoryShort } from "~/types";

export async function useCatalogCategoryResolver(slugs: MaybeRef<string[]>) {
  const app = useNuxtApp();
  const catalogLinker = useCatalogLinker();
  const { resolveCategoryByUrlPathMultiLocale, resolveCategory } =
    useCategoryResolver();

  const redirectToCategory = ref<CategoryShort | undefined>();

  const {
    data: category,
    status: categoriesSearchStatus,
    error: categoriesSearchError,
    refresh: fetchCategories,
  } = await useSyncCachedAsyncData(
    CATALOG_PAGE_CATEGORY + "-" + unref(slugs).join("-"),
    async () => {
      const category = await resolveCategory<CategoryFull>(
        unref(slugs).join("/"),
        "CategoryFullProj",
      );

      if (!category) {
        // Try to resolve category in other locales
        redirectToCategory.value = await resolveCategoryByUrlPathMultiLocale(
          unref(slugs).join("/"),
        );

        // If we found category by current path in different locale, redirect to correct url
        throw new Error(`Target category not found`);
      } else {
        return category;
      }
    },
    { dedupe: "defer" },
  );

  app.runWithContext(() => {
    if (redirectToCategory.value) {
      navigateTo(catalogLinker.categoryLink(redirectToCategory.value));
    }

    if (category.value?.redirect_code) {
      navigateTo(catalogLinker.categoryLink(category.value));
    }

    if (
      !category.value &&
      (categoriesSearchStatus.value === "success" ||
        categoriesSearchError.value)
    ) {
      throw showError({
        statusCode: 404,
        statusMessage: "Page Not Found",
        fatal: true,
      });
    }
  });

  return {
    category,
    categoriesSearchStatus,
    categoriesSearchError,
    fetchCategories,
    redirectToCategory,
  };
}
